/* eslint-disable react-refresh/only-export-components */
// Co-locating the provider, hook, and types is intentional; fast-refresh
// granularity is irrelevant for app-wide context.
import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import type { PSAAdapter, RawRow } from "@/lib/adapters/base";
import type { DetectionResult } from "@/lib/adapters/registry";
import { runPipeline } from "@/lib/processing/pipeline";
import { configStore } from "@/lib/storage/localStorage";
import type { CatalogKitConfig, ProcessingResult } from "@/types";

/** A parsed upload awaiting an adapter choice. */
export interface ParsedUpload {
  fileName: string;
  headers: string[];
  rawRows: RawRow[];
  detection: DetectionResult[];
}

/** The inputs needed to re-run processing live when config changes. */
export interface ProcessingSource {
  fileName: string;
  rawRows: RawRow[];
  adapter: PSAAdapter;
}

interface AppContextValue {
  config: CatalogKitConfig;
  setConfig: (config: CatalogKitConfig) => void;
  upload: ParsedUpload | null;
  setUpload: (upload: ParsedUpload | null) => void;
  result: ProcessingResult | null;
  setResult: (result: ProcessingResult | null) => void;
  /** Record the chosen source + result so config changes can reprocess. */
  setProcessed: (source: ProcessingSource, result: ProcessingResult) => void;
  source: ProcessingSource | null;
  reprocessing: boolean;
  /** Product ID to scroll to + briefly highlight in the data table (cross-page). */
  highlightProductId: string | null;
  setHighlightProductId: (productId: string | null) => void;
  reset: () => void;
}

const AppContext = createContext<AppContextValue | null>(null);

/** Provides app-wide state: config (persisted), the current upload, the processing source + result. */
export function AppProvider({ children }: { children: ReactNode }): JSX.Element {
  const [config, setConfigState] = useState<CatalogKitConfig>(() => configStore.load());
  const [upload, setUpload] = useState<ParsedUpload | null>(null);
  const [result, setResult] = useState<ProcessingResult | null>(null);
  const [source, setSource] = useState<ProcessingSource | null>(null);
  const [reprocessing, setReprocessing] = useState(false);
  const [highlightProductId, setHighlightProductId] = useState<string | null>(null);
  const sourceRef = useRef<ProcessingSource | null>(null);

  const setProcessed = useCallback((nextSource: ProcessingSource, nextResult: ProcessingResult) => {
    sourceRef.current = nextSource;
    setSource(nextSource);
    setResult(nextResult);
  }, []);

  const setConfig = useCallback((next: CatalogKitConfig) => {
    setConfigState(next);
    configStore.save(next);
    const current = sourceRef.current;
    if (current) {
      // Live re-process; defer so the loading skeleton can paint first.
      setReprocessing(true);
      setTimeout(() => {
        setResult(runPipeline(current.rawRows, current.adapter, next));
        setReprocessing(false);
      }, 0);
    }
  }, []);

  const reset = useCallback(() => {
    sourceRef.current = null;
    setSource(null);
    setUpload(null);
    setResult(null);
    setHighlightProductId(null);
  }, []);

  const value = useMemo<AppContextValue>(
    () => ({
      config,
      setConfig,
      upload,
      setUpload,
      result,
      setResult,
      setProcessed,
      source,
      reprocessing,
      highlightProductId,
      setHighlightProductId,
      reset,
    }),
    [config, setConfig, upload, result, setProcessed, source, reprocessing, highlightProductId, reset],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

/** Access app-wide state. Throws if used outside an {@link AppProvider}. */
export function useAppContext(): AppContextValue {
  const context = useContext(AppContext);
  if (context === null) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
}
